/**
 * @file ShopSettingsSections.tsx
 * @description Секции формы «Магазин» (phase19.31; page-analysis-c, макет
 * (а)): «Основное», «Контакты для клиентов», «Заказы с сайта».
 *
 * Общий контракт секций — `value/onChange` черновика формы
 * (`ShopSettingsDraft`), как у `CarrierMainFields`: сохраняет одна кнопка
 * страницы. Подписи по-русски («Имя в адресе», а не «Slug»), обычным
 * регистром; ответственный — по имени, а не по сырому идентификатору.
 */

import type { ReactNode } from "react";

import { ShopTimeZoneField } from "./ShopTimeZoneField";
import type { ShopSettingsDraft, ShopSettingsErrors } from "./useShopSettingsForm";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { SelectField } from "../../../components/ui/SelectField";
import { StatusPill } from "../../../components/ui/StatusPill";
import type { CheckoutSettingsDto } from "../../../lib/api-checkout-settings";
import type { ShopSettingsDto, ShopTimeZoneOption } from "../../../lib/api-shop-settings";

interface SectionProps {
  value: ShopSettingsDraft;
  onChange: (part: Partial<ShopSettingsDraft>) => void;
  errors: ShopSettingsErrors;
  disabled: boolean;
}

/** Секция формы: заголовок 18/600 и поля колонкой. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="type-section-title text-on-surface">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Строка «подпись — значение» только для чтения. Подпись — той же шкалы, что
 * `Label` у полей формы («Часовой пояс»): раньше была мельче (12px против
 * 14px) — дефект приёмки 2026-10-02, SC43 «одна шкала».
 */
function ReadRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
      <span className="text-sm font-medium leading-none text-on-surface-variant sm:w-40 sm:shrink-0">{label}</span>
      <span className="type-body min-w-0 text-on-surface">{children}</span>
    </div>
  );
}

/** «Основное»: название, адрес, статус (только чтение) и часовой пояс. */
export function ShopMainSection({
  settings,
  timeZones,
  value,
  onChange,
  errors,
  disabled,
}: SectionProps & { settings: ShopSettingsDto; timeZones: ShopTimeZoneOption[] }) {
  return (
    <Section title="Основное">
      <ReadRow label="Название">{settings.displayName}</ReadRow>
      <ReadRow label="Имя в адресе">
        <span className="font-mono">{settings.slug}</span>
      </ReadRow>
      <ReadRow label="Статус">
        {settings.isActive ? <StatusPill tone="success">Активен</StatusPill> : <StatusPill tone="neutral">Отключён</StatusPill>}
      </ReadRow>
      <p className="type-caption text-on-surface-variant">Название и адрес магазина меняет администратор платформы.</p>
      <ShopTimeZoneField
        value={value.timeZone}
        options={timeZones}
        onChange={(timeZone) => onChange({ timeZone })}
        error={errors.timeZone}
        disabled={disabled}
      />
    </Section>
  );
}

/** «Контакты для клиентов»: почта магазина. */
export function ShopContactsSection({ value, onChange, errors, disabled }: SectionProps) {
  return (
    <Section title="Контакты для клиентов">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="shop-contact-email">Почта для клиентов</Label>
        <Input
          id="shop-contact-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="shop@example.ru"
          value={value.contactEmail}
          disabled={disabled}
          aria-invalid={errors.contactEmail ? true : undefined}
          aria-describedby="shop-contact-email-hint"
          onChange={(e) => onChange({ contactEmail: e.target.value })}
        />
        <p
          id="shop-contact-email-hint"
          className={errors.contactEmail ? "type-caption text-[var(--destructive-text)]" : "type-caption text-on-surface-variant"}
        >
          {errors.contactEmail ??
            (value.contactEmail.trim()
              ? "Видна клиентам в письмах и личном кабинете. Оставьте пустой, чтобы убрать контакт."
              : "Почта не указана — клиенты не увидят контакт магазина в письмах.")}
        </p>
      </div>
    </Section>
  );
}

/** «Заказы с сайта»: ответственный по умолчанию — по имени. */
export function ShopOrdersSection({ checkout, value, onChange, disabled }: SectionProps & { checkout: CheckoutSettingsDto }) {
  const options = [
    { value: "", label: "Не назначен (автовыбор)" },
    ...checkout.candidates.map((u) => ({ value: u.id, label: u.displayName ?? u.email })),
  ];
  return (
    <Section title="Заказы с сайта">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="shop-order-owner">Ответственный по умолчанию</Label>
        <SelectField
          id="shop-order-owner"
          value={value.ownerId}
          onChange={(ownerId) => onChange({ ownerId })}
          options={options}
          placeholder="Не назначен (автовыбор)"
          ariaLabel="Ответственный за заказы с сайта"
          disabled={disabled}
        />
        <p className="type-caption text-on-surface-variant">
          {checkout.candidates.length === 0
            ? "В магазине нет сотрудников, которым можно передать заказы. Пригласите их в разделе «Сотрудники»."
            : "На него назначаются заказы, оформленные на сайте. Без назначения заказ уйдёт первому сотруднику магазина."}
        </p>
      </div>
    </Section>
  );
}
